import React, { useContext } from 'react';
import styled from 'styled-components';
import Row from 'components/Common/Row';

import FlexCentered from 'components/Common/FlexCentered';
import Expectation, {
  OverrideTooltip,
} from 'components/DriverAX/components/Expectation';
import Loading from 'components/Loading';
import Tooltip from 'components/Tooltip';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import usePopup from 'hooks/usePopup';
import Button from 'kingpin/atoms/Button';
import Card from 'kingpin/atoms/Card';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import useBonusEligibility from '../../hooks/useBonusEligibility';

const QUALIFIED_BACKGROUND = 'var(--color-success-bg)';
const DISQUALIFIED_BACKGROUND = 'var(--color-danger-bg)';

const OuterDiv = styled.div<{ isOpen: boolean; isQualified: boolean }>`
  width: 100%;
  border-radius: 8px;
  padding: 0px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  border-width: 1px;
  border-style: solid;

  // state dependant - isQualified
  border-color: ${(props) =>
    props.isQualified
      ? 'var(--color-success-border)'
      : 'var(--color-danger-border)'};
  background-color: ${(props) =>
    props.isQualified ? QUALIFIED_BACKGROUND : DISQUALIFIED_BACKGROUND};
`;

const InnerDiv = styled.div<{ isQualified: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 0;

  border-top-width: 1px;
  border-top-style: solid;
  border-top-color: ${(props) =>
    props.isQualified
      ? 'var(--color-success-border)'
      : 'var(--color-danger-border)'};
`;

const OverrideBadge = ({
  persistedOverride,
  reasonCodes,
}: {
  persistedOverride: BonusTypes.Overrides.PersistedQualifier;
  reasonCodes?: string[];
}) => {
  return (
    <div style={{ cursor: 'help' }}>
      <Tooltip
        content={
          persistedOverride.note ? (
            <OverrideTooltip
              {...persistedOverride.note}
              reasonCodes={reasonCodes}
            />
          ) : undefined
        }
      >
        <Icon icon={'info'} color={'var(--color-status-warning)'} />
      </Tooltip>
    </div>
  );
};

const BonusEligibilitySlideoutSection = ({
  bonusProgram,
  rewardOutput,
  overrides,
}: {
  bonusProgram: BonusTypes.BonusProgram;
  rewardOutput: BonusTypes.Rewards.AnyDriverRewardsOutput;
  overrides?: BonusTypes.Overrides.Persisted;
}) => {
  const { isOpen, open, close } = usePopup();
  const { isQualified, expectations, StatusIcon } = useBonusEligibility({
    bonusProgram,
    rewardOutput,
  });

  const persistedOverride: BonusTypes.Overrides.PersistedQualifier | undefined =
    overrides ? overrides.qualifierOverride || undefined : undefined;

  return (
    <>
      <OuterDiv isOpen={isOpen} isQualified={isQualified}>
        <Row spaceBetween centerAlign style={{ height: 56 }}>
          <Row centerAlign style={{ gap: 8 }}>
            {StatusIcon}
            <Typography.Header type={'H5'}>
              {`Bonus Eligibility: ${isQualified ? 'Unlocked' : 'Disqualified'}`}
            </Typography.Header>
            {persistedOverride && (
              <OverrideBadge
                persistedOverride={persistedOverride}
                reasonCodes={overrides?.reasonCodes}
              />
            )}
          </Row>
          <Row centerAlign style={{ gap: 8 }}>
            <Button
              size={'X-Small - Circle'}
              type={'Ghost'}
              onClick={isOpen ? close : open}
              icon={isOpen ? 'chevron-up' : 'chevron-down'}
              iconColor={'var(--color-icon-secondary)'}
            />
          </Row>
        </Row>

        {isOpen && (
          <InnerDiv isQualified={isQualified}>
            {expectations.map((e) => (
              <Expectation {...e} variant={'slideout'} />
            ))}
          </InnerDiv>
        )}
      </OuterDiv>
    </>
  );
};

const Gate = () => {
  const { isLoading, bonusOverview } = useContext(BonusOverviewContext);

  if (isLoading) {
    return (
      <Card style={{ height: 56 }}>
        <Loading />
      </Card>
    );
  }

  if (!bonusOverview) {
    return (
      <FlexCentered style={{ height: 56 }}>
        <Typography.Header type={'H4'}>
          Bonus information not found
        </Typography.Header>
      </FlexCentered>
    );
  }

  return (
    <BonusEligibilitySlideoutSection
      rewardOutput={bonusOverview.rewardOutput}
      bonusProgram={bonusOverview.bonusProgram}
      overrides={bonusOverview.overrides}
    />
  );
};

export default Gate;
